import { useEffect, useState } from "react";
import api, { apiError } from "../../lib/api";
import { toast } from "sonner";

export const DealNotes = ({ dealId }) => {
  const [notes, setNotes] = useState([]);
  const [body, setBody] = useState("");
  const [link, setLink] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);

  const load = () => api.get(`/deals/${dealId}/my-notes`).then((r) => setNotes(r.data)).catch(() => {});
  useEffect(() => { load(); }, [dealId]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!body.trim() && !link.trim() && !file) { toast.error("Add a note, a link, or a file."); return; }
    setBusy(true);
    try {
      if (editId) {
        await api.put(`/deals/${dealId}/notes/${editId}`, { body, link });
      } else {
        const fd = new FormData();
        fd.append("body", body);
        fd.append("link", link);
        if (file) fd.append("file", file);
        await api.post(`/deals/${dealId}/notes`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(editId ? "Note updated." : "Shared with Solera. Visible only to the Solera team.");
      setBody(""); setLink(""); setFile(null); setEditId(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const edit = (n) => { setEditId(n.note_id); setBody(n.body || ""); setLink(n.link || ""); setFile(null); };

  const del = async (n) => {
    if (!window.confirm("Delete this note?")) return;
    try { await api.delete(`/deals/${dealId}/notes/${n.note_id}`); toast.success("Note deleted."); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const download = async (n) => {
    try {
      const res = await api.get(`/deals/${dealId}/notes/${n.note_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = n.file_name; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="mt-16 max-w-3xl" data-testid="deal-notes">
      <span className="t-label">Share notes with Solera, private</span>
      <p className="t-cap text-muted-s mt-2 measure">
        Add intel, a link, or a file for this deal. Your notes are visible only to the Solera team, not to other members.
      </p>
      <div className="mt-6 space-y-0">
        {notes.map((n) => (
          <div key={n.note_id} className="hairline py-5" data-testid={`note-${n.note_id}`}>
            {n.body && <p className="t-body text-bone whitespace-pre-wrap">{n.body}</p>}
            {n.link && <a href={n.link} target="_blank" rel="noreferrer" className="t-small text-tint underline break-all mt-1 block" data-testid={`note-link-${n.note_id}`}>{n.link}</a>}
            {n.file_name && <button className="t-small text-tint underline mt-1 block" onClick={() => download(n)} data-testid={`note-file-${n.note_id}`}>{n.file_name}</button>}
            <div className="flex items-center gap-4 mt-2">
              <span className="t-cap text-muted-s fig">{n.created_at?.slice(0, 10)}{n.updated_at !== n.created_at ? ", edited" : ""}</span>
              <button className="t-label hover:!text-[#E8E6E1]" onClick={() => edit(n)} data-testid={`note-edit-${n.note_id}`}>Edit</button>
              <button className="t-label hover:!text-[#E8E6E1]" onClick={() => del(n)} data-testid={`note-delete-${n.note_id}`}>Delete</button>
            </div>
          </div>
        ))}
        {notes.length === 0 && <p className="t-small text-muted-s hairline py-5">No notes yet.</p>}
      </div>
      <div className="mt-6 space-y-3">
        <textarea className="input-solera w-full" rows={3} placeholder="Your note or intel for Solera"
          value={body} onChange={(e) => setBody(e.target.value)} data-testid="note-body-input" />
        <input className="input-solera w-full" placeholder="Add a link, optional"
          value={link} onChange={(e) => setLink(e.target.value)} data-testid="note-link-input" />
        {!editId && (
          <input type="file" className="t-small text-muted-s block" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="note-file-input" />
        )}
        <div className="flex gap-3">
          {editId && <button className="btn-ghost" onClick={() => { setEditId(null); setBody(""); setLink(""); setFile(null); }} data-testid="note-cancel">Cancel</button>}
          <button className="btn-primary" disabled={busy} onClick={submit} data-testid="note-submit">
            {editId ? "Save note" : "Share with Solera"}
          </button>
        </div>
      </div>
    </div>
  );
};
